import type { User } from '../../types';
import type { EnrollmentListRow } from '../../types/sessions';
import type { IdCardAudience, IdCardField } from '../../types/idCards';
import { fmtClassName, fmtDate } from '../../utils/pdf';

/**
 * The person a card is printed for — their name, photo and the values of
 * every detail a card may show. Built from the enrollments list (students)
 * or the employees list (staff); the design decides which details print.
 */
export interface CardPerson {
  key: string;
  name: string;
  /** Photo filename on record, if any (fetched as a data URL to draw). */
  photoFilename: string | null;
  values: Partial<Record<IdCardField, string>>;
}

export const initialsOf = (name: string): string =>
  name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('') || '?';

/** "8 · A" — class and section. */
const classSection = (row: EnrollmentListRow): string =>
  [fmtClassName(row.class?.name), row.section?.name].filter(Boolean).join(' · ');

export const personFromEnrollment = (row: EnrollmentListRow, sessionName?: string): CardPerson => {
  const s = row.student;
  return {
    key: row._id,
    name: s?.name ?? '',
    photoFilename: s?.photoFilename ?? null,
    values: {
      grNumber: s?.grNumber,
      classSection: classSection(row),
      rollNumber: row.rollNumber != null ? String(row.rollNumber) : undefined,
      fatherName: s?.fatherName,
      dob: s?.dob ? fmtDate(s.dob) : undefined,
      phone: s?.fatherPhone || s?.phone,
      session: sessionName,
    },
  };
};

export const personFromStaff = (user: User): CardPerson => ({
  key: user._id,
  name: user.name,
  // Staff have no photo on record yet; their card shows initials.
  photoFilename: null,
  values: {
    staffCode: user.userCode,
    designation: user.roleId?.name,
    phone: user.phone,
    email: user.email,
  },
});

/** Shown in the designer until the school has someone to preview with. */
export const samplePerson = (audience: IdCardAudience): CardPerson => audience === 'student'
  ? {
    key: 'sample',
    name: 'Ayesha Rehman',
    photoFilename: null,
    values: {
      grNumber: '000128', classSection: 'Class 6 · B', rollNumber: '14', fatherName: 'Abdul Rehman',
      dob: '12 Mar 2015', phone: '0300 1234567', session: '2026-27',
    },
  }
  : {
    key: 'sample',
    name: 'Sana Tariq',
    photoFilename: null,
    values: { staffCode: 'FAT-000042', designation: 'Senior Teacher', phone: '0321 7654321', email: 'sana.tariq@school.edu.pk' },
  };
